import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, List, Card, Button, Empty, Typography, Space, Popconfirm, message, Spin } from 'antd';
import { DownloadOutlined, DeleteOutlined, ReloadOutlined } from '@ant-design/icons';

const { Text } = Typography;
const COVER_ANIMATE_PRIMER_SECONDS = 4;
const COVER_ANIMATE_MULTI_PASS_ENABLED = false;

export interface MarketingVideoHistoryItem {
  id: string;
  language?: string;
  createdAt?: string;
  model?: string;
  mode?: string;
  prompt?: string;
  variant?: string;
  durationSeconds?: number;
  generationPasses?: number;
  finalDurationSeconds?: number;
  billedDurationSeconds?: number;
  aspectRatio?: string;
  resolution?: string;
  generateAudio?: boolean;
  sampleCount?: number;
  signedPreviewUrl?: string | null;
}

interface MarketingVideoHistoryScreenProps {
  open: boolean;
  onClose: () => void;
  apiBase: string;
  bookId: string;
  userId: string;
  language?: string;
}

const resolveGenerationPasses = (mode?: string, generationPasses?: number): number => {
  if (typeof generationPasses === 'number' && Number.isFinite(generationPasses) && generationPasses > 0) {
    return Math.floor(generationPasses);
  }
  return (mode || '').toLowerCase() === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED ? 2 : 1;
};

const resolveFinalDurationSeconds = (
  durationSeconds?: number,
  mode?: string,
  generationPasses?: number,
  finalDurationSeconds?: number
): number | null => {
  if (
    typeof finalDurationSeconds === 'number' &&
    Number.isFinite(finalDurationSeconds) &&
    finalDurationSeconds > 0
  ) {
    return Math.floor(finalDurationSeconds);
  }
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }
  return Math.floor(durationSeconds) * resolveGenerationPasses(mode, generationPasses);
};

const resolveBilledDurationSeconds = (
  durationSeconds?: number,
  mode?: string,
  billedDurationSeconds?: number
): number | null => {
  if (
    typeof billedDurationSeconds === 'number' &&
    Number.isFinite(billedDurationSeconds) &&
    billedDurationSeconds > 0
  ) {
    return Math.floor(billedDurationSeconds);
  }
  if (typeof durationSeconds !== 'number' || !Number.isFinite(durationSeconds) || durationSeconds <= 0) {
    return null;
  }
  if ((mode || '').toLowerCase() === 'cover_animate' && COVER_ANIMATE_MULTI_PASS_ENABLED) {
    return Math.floor(durationSeconds) + COVER_ANIMATE_PRIMER_SECONDS;
  }
  return Math.floor(durationSeconds);
};

const renderVariantLabel = (variant?: string | null): string | null => {
  const normalized = (variant || '').toLowerCase().trim();
  if (!normalized || normalized === 'final') return null;
  if (normalized === 'system_reversed_forward') return 'System Reversed (Forward)';
  if (normalized === 'ai_original_backwards') return 'AI Original (Backward)';
  return normalized;
};

const buildHistoryStreamUrl = ({
  apiBase,
  bookId,
  historyId,
  userId,
  download = false,
  filename,
}: {
  apiBase: string;
  bookId: string;
  historyId: string;
  userId: string;
  download?: boolean;
  filename?: string;
}) => {
  const params = new URLSearchParams();
  params.set('userId', userId);
  if (download) params.set('download', '1');
  if (filename) params.set('filename', filename);
  return `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history/${encodeURIComponent(historyId)}/stream?${params.toString()}`;
};

const MarketingVideoHistoryScreen: React.FC<MarketingVideoHistoryScreenProps> = ({
  open,
  onClose,
  apiBase,
  bookId,
  userId,
  language,
}) => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState<MarketingVideoHistoryItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchHistory = useCallback(async (targetPage = 1, append = false) => {
    if (!open || !bookId || !userId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('userId', userId);
      params.set('page', String(targetPage));
      params.set('pageSize', '24');
      if (language) params.set('language', language);
      const response = await fetch(
        `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history?${params.toString()}`
      );
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const data = await response.json();
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems(prev => (append ? [...prev, ...nextItems] : nextItems));
      setPage(targetPage);
      setHasMore(Boolean(data?.hasMore));
    } catch (error) {
      console.error('Failed to load marketing video history', error);
      message.error('Failed to load video history');
    } finally {
      setLoading(false);
    }
  }, [apiBase, bookId, language, open, userId]);

  useEffect(() => {
    if (open) {
      void fetchHistory(1, false);
    } else {
      setItems([]);
      setLoading(false);
      setPage(1);
      setHasMore(false);
      setDownloadingId(null);
      setDeletingId(null);
    }
  }, [open, fetchHistory]);

  const historyTitle = useMemo(() => `Video History (${items.length})`, [items.length]);

  const handleDownload = useCallback(
    async (item: MarketingVideoHistoryItem) => {
      if (!item?.id) return;
      setDownloadingId(item.id);
      try {
        const signedUrl =
          item.signedPreviewUrl ||
          buildHistoryStreamUrl({
            apiBase,
            bookId,
            historyId: item.id,
            userId,
            download: true,
            filename: `marketing_video_${item.id}.mp4`,
          });
        const anchor = document.createElement('a');
        anchor.href = signedUrl;
        anchor.download = `marketing_video_${item.id}.mp4`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      } catch (error) {
        console.error('Failed to download marketing video', error);
        message.error('Failed to download video');
      } finally {
        setDownloadingId(null);
      }
    },
    [apiBase, bookId, userId]
  );

  const handleDelete = useCallback(
    async (item: MarketingVideoHistoryItem) => {
      if (!item?.id) return;
      setDeletingId(item.id);
      try {
        const response = await fetch(
          `${apiBase}/api/books/${encodeURIComponent(bookId)}/marketing/videos/history/${encodeURIComponent(item.id)}`,
          {
            method: 'DELETE',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ userId }),
          }
        );
        if (!response.ok) {
          throw new Error(await response.text());
        }
        setItems((prev) => prev.filter((entry) => entry.id !== item.id));
        message.success('Deleted video from history');
      } catch (error) {
        console.error('Failed to delete marketing video history item', error);
        message.error('Failed to delete video');
      } finally {
        setDeletingId(null);
      }
    },
    [apiBase, bookId, userId]
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      width={1000}
      title={historyTitle}
      okText="Close"
      cancelButtonProps={{ style: { display: 'none' } }}
      destroyOnClose
    >
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'flex-end' }}>
        <Button icon={<ReloadOutlined />} onClick={() => void fetchHistory(1, false)}>
          Refresh
        </Button>
      </div>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty description="No marketing videos yet." />
      ) : (
        <List
          grid={{ gutter: 16, xs: 1, sm: 1, md: 2, lg: 2, xl: 3, xxl: 3 }}
          dataSource={items}
          renderItem={(item) => (
            <List.Item key={item.id}>
              {(() => {
                const generationPasses = resolveGenerationPasses(item.mode, item.generationPasses);
                const finalDurationSeconds = resolveFinalDurationSeconds(
                  item.durationSeconds,
                  item.mode,
                  item.generationPasses,
                  item.finalDurationSeconds
                );
                const billedDurationSeconds = resolveBilledDurationSeconds(
                  item.finalDurationSeconds ?? item.durationSeconds,
                  item.mode,
                  item.billedDurationSeconds
                );
                const variantLabel = renderVariantLabel(item.variant);
                return (
              <Card
                size="small"
                title={`Video ${item.id.slice(0, 8)}`}
                extra={
                  <Space>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={downloadingId === item.id}
                      onClick={() => void handleDownload(item)}
                    />
                    <Popconfirm
                      title="Delete this video?"
                      okText="Delete"
                      cancelText="Cancel"
                      onConfirm={() => void handleDelete(item)}
                    >
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        loading={deletingId === item.id}
                      />
                    </Popconfirm>
                  </Space>
                }
              >
                <div style={{ marginBottom: 8 }}>
                  <Text type="secondary">
                    {item.createdAt ? new Date(item.createdAt).toLocaleString() : 'Unknown time'}
                  </Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>
                    {item.mode || 'mode?'} | {item.model || 'model?'}
                    {variantLabel ? ` | ${variantLabel}` : ''}
                  </Text>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <Text>
                    {finalDurationSeconds ?? '?'}s final
                    {generationPasses > 1 ? ` (${COVER_ANIMATE_PRIMER_SECONDS}s primer pass)` : ''}
                    {billedDurationSeconds ? ` | ${billedDurationSeconds}s billed` : ''} |{' '}
                    {item.aspectRatio || '?'} | {item.resolution || '?'} | {item.generateAudio ? 'Audio On' : 'Audio Off'}
                  </Text>
                </div>
                {item.prompt ? (
                  <div style={{ marginBottom: 8 }}>
                    <Text type="secondary">{item.prompt}</Text>
                  </div>
                ) : null}
                <video
                  style={{ width: '100%', maxHeight: 220, borderRadius: 6, background: '#000' }}
                  src={
                    buildHistoryStreamUrl({
                      apiBase,
                      bookId,
                      historyId: item.id,
                      userId,
                    }) || item.signedPreviewUrl || undefined
                  }
                  controls
                  preload="metadata"
                />
              </Card>
                );
              })()}
            </List.Item>
          )}
        />
      )}
      {hasMore && !loading ? (
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <Button onClick={() => void fetchHistory(page + 1, true)}>Load More</Button>
        </div>
      ) : null}
    </Modal>
  );
};

export default MarketingVideoHistoryScreen;
